/**
 * Reporting / analytics aggregates — tickets slice from reporting-service.
 */

import { getSlaSettings } from "@/api/slaSettingsApi";
import { fetchTicketDashboard } from "@/api/reportingTicketApi";
import { evaluateTicketSlaBreach } from "@/lib/slaBreach";

/**
 * @param {{ startDate?: string, endDate?: string }} [range]
 * @returns {Promise<{ data: { tickets: Record<string, unknown> } }>}
 */
export async function getReportingDashboard(range = {}) {
  const now = Date.now();
  const [ticketDashboard, slaRes] = await Promise.all([
    fetchTicketDashboard({ startDate: range.startDate, endDate: range.endDate }),
    getSlaSettings(),
  ]);
  const payload = ticketDashboard?.success ? ticketDashboard : {};
  const overview = payload.overview ?? {};
  const statusCounts = Array.isArray(payload.status_counts) ? payload.status_counts : [];
  const ticketStatusShare = Array.isArray(payload.status_share) ? payload.status_share : [];
  const volumeWeek = Array.isArray(payload.volume) ? payload.volume : [];
  const volumeMax = Number.isFinite(payload.volume_max) ? payload.volume_max : 1;
  const agentRows = Array.isArray(payload.agent_rows) ? payload.agent_rows : [];
  const openTickets = Array.isArray(payload.open_tickets) ? payload.open_tickets : [];
  const settings = slaRes?.data ?? {};

  let ticketSlaBreaches = 0;
  for (const t of openTickets) {
    if (evaluateTicketSlaBreach(t, settings, now).breached) ticketSlaBreaches += 1;
  }

  const ticketsOpen = Number(overview.open_count) || 0;
  const ticketsPending = Number(overview.pending_count) || 0;
  const ticketsOnHold = Number(overview.on_hold_count) || 0;
  const ticketsResolved = Number(overview.resolved_count) || 0;
  const ticketTotal = Number(overview.ticket_total) || 0;
  const ticketSum = ticketsOpen + ticketsPending + ticketsOnHold + ticketsResolved;
  const activeTickets = ticketsOpen + ticketsPending + ticketsOnHold;

  const ticketKpis = [
    {
      id: "tk-active",
      label: "Active tickets",
      value: String(activeTickets),
      trend: `${ticketsOpen} open · ${ticketsPending} pending · ${ticketsOnHold} on hold`,
      trendUp: ticketSlaBreaches === 0,
      icon: "ri-ticket-2-line",
      color: "#f59e0b",
    },
    {
      id: "tk-total",
      label: "Tickets in queue",
      value: String(ticketTotal),
      trend: `${ticketsResolved} resolved total`,
      trendUp: true,
      icon: "ri-stack-line",
      color: "#ea580c",
    },
    {
      id: "tk-sla",
      label: "Tickets — SLA at risk",
      value: String(ticketSlaBreaches),
      trend: "Non-resolved breaching ticket SLA",
      trendUp: ticketSlaBreaches === 0,
      icon: "ri-alarm-warning-line",
      color: "#e11d48",
    },
    {
      id: "tk-resolved-pct",
      label: "Resolved share",
      value: ticketSum > 0 ? `${Math.round((ticketsResolved / ticketSum) * 100)}%` : "—",
      trend: ticketSum > 0 ? `${ticketsResolved} of ${ticketSum} closed` : "No tickets yet",
      trendUp: ticketSum === 0 || ticketsResolved / ticketSum > 0.5,
      icon: "ri-checkbox-circle-line",
      color: "#10b981",
    },
  ];

  const agentTicketRows = agentRows.map((r) => {
    const name = String(r.name ?? "Unassigned");
    let atRisk = 0;
    for (const t of openTickets) {
      if (String(t.assigneeName ?? "Unassigned") !== name) continue;
      if (evaluateTicketSlaBreach(t, settings, now).breached) atRisk += 1;
    }
    return {
      name,
      ticketsLinked: Number(r.ticketsLinked) || 0,
      ticketsActiveLinked: Number(r.ticketsActiveLinked) || 0,
      ticketSlaAtRisk: atRisk,
    };
  });

  return {
    data: {
      tickets: {
        kpis: ticketKpis,
        ticketStatusShare,
        statusCounts,
        volumeWeek,
        volumeMax,
        agentTicketRows,
        meta: {
          ticketTotal,
          activeTickets,
          ticketsOpen,
          ticketsPending,
          ticketsOnHold,
          ticketsResolved,
          ticketSlaBreaches,
        },
      },
    },
  };
}
